import { useContext, useEffect, useState } from 'react';

import { Alert } from '@zillow/constellation';
import { Form, TextButton } from '@zillow/constellation-10';

import { FreemiumContext } from '../../../components/Freemium/context';
import { ModularListingContext } from '../../../containers/CreateListingPage/context';
import { WizardContext } from '../../../components/Wizard/context';

import { useSelector } from '../../../selectors/useSelector';

import { useGetPremiumPricingGuidance } from '../../../hooks/queries/freemium';

import { PRODUCT_TYPE_NAME_TO_ID } from '../../../constants/FreemiumConstants';
import { DISPLAY_SERVER_ERROR_AT } from '../../../constants/WizardConstants';

import numberUtils from '../../../utils/numberUtils';

import WizardAlert from '../../../components/WizardAlert';
import WizardTipCardContainer from '../../../components/WizardTipCardContainer';
import PageWizard from '../../../components/Wizard/PageWizard';

import FormSectionContainer from './components/FormSectionContainer';
import WizardTipContent from './components/WizardTipContent';
import PremiumPriceTipContent from './components/PremiumPriceTipContent';
import PremiumPricingGuidanceModal from '../../Modal/PremiumPricingGuidanceModal';

import { WizardFlexContainer } from '../../../styles/wizard';

import type { FormEvent, ReactNode } from 'react';
import type { BreakpointToken } from '@/styled-system/tokens'; // eslint-disable-line no-restricted-imports

type Props = {
  onGoToPrevStep: () => void;
  onGoToNextStep: () => void;
  onExitWizard: () => void;
  wizardTitle: ReactNode;
  progressStepper: ReactNode;
  totalSteps: number;
  currentStepIndex: number;
  showDesktopViewBreakpoint: BreakpointToken;
};

const MonthlyRentAndSecurityDeposit = ({
  onGoToNextStep,
  onGoToPrevStep,
  onExitWizard,
  wizardTitle,
  progressStepper,
  totalSteps,
  currentStepIndex,
  showDesktopViewBreakpoint,
  ...rest
}: Props): JSX.Element => {
  const {
    // @ts-expect-error
    wizardState: { isFetchingData },
  } = useContext(WizardContext);

  const {
    modularListingState: { serverErrorObj },
  } = useContext(ModularListingContext);

  const {
    freemiumState: { canAccessTopOfFunnelPremium },
  } = useContext(FreemiumContext);

  const {
    properties: {
      currentProperty: {
        city,
        floorplans,
        listingAlias,
        rentZestimate,
        freemium: {
          activeProduct: { type: activeProductType },
        },
      },
    },
  } = useSelector((state) => state);

  const isActiveProductTypePremium = activeProductType === PRODUCT_TYPE_NAME_TO_ID.PREMIUM;

  const price = floorplans[0]?.price;
  const {
    refetch: refetchPremiumPricingGuidance,
    data: pricingGuidance,
    error: pricingGuidanceError,
  } = useGetPremiumPricingGuidance(
    { listingId: listingAlias },
    {
      enabled: Boolean(listingAlias && canAccessTopOfFunnelPremium && isActiveProductTypePremium),
    },
  );

  const [clickedNext, setClickedNext] = useState(false);
  const [clickedBackNext, setClickedBack] = useState(false);

  useEffect(() => {
    const { fieldName } = serverErrorObj;

    if (!fieldName && !isFetchingData) {
      checkNavToStep();
    }
  }, [isFetchingData]);

  useEffect(() => {
    if (listingAlias && canAccessTopOfFunnelPremium && isActiveProductTypePremium) {
      refetchPremiumPricingGuidance();
    }
  }, [price]);

  const checkNavToStep = () => {
    if (clickedNext) {
      onNextClick();
    }

    if (clickedBackNext) {
      onBackClick();
    }
  };

  const onNextClick = () => {
    if (isFetchingData) {
      setClickedNext(true);
      return;
    }

    onGoToNextStep();
  };

  const onBackClick = () => {
    if (isFetchingData) {
      setClickedBack(true);
      return;
    }

    onGoToPrevStep();
  };

  const header = 'About Rent Zestimate';
  let premiumHeader = `Optimize your listing with ${city} pricing data and insights`;

  if (isActiveProductTypePremium && pricingGuidance && pricingGuidance?.marketMedian?.price) {
    const {
      beds,
      marketMedian: { price: medianPrice },
      propertyType: { display: propertyTypeDisplay },
      zip,
    } = pricingGuidance;
    if ((beds || beds === 0) && propertyTypeDisplay && zip && medianPrice) {
      if (beds === 0) {
        premiumHeader = `The median rental price for studios in ${zip} is ${numberUtils.asCurrency(Number(numberUtils.removeDollarSign(medianPrice)))}`;
      } else {
        premiumHeader = `The median rental price for ${beds} bedroom ${propertyTypeDisplay.toLowerCase()}s in ${zip} is ${numberUtils.asCurrency(Number(numberUtils.removeDollarSign(medianPrice)))}`;
      }
    }
  }

  return (
    <PageWizard
      title=""
      wizardTitle={wizardTitle}
      progressStepper={progressStepper}
      totalSteps={totalSteps}
      currentStepIndex={currentStepIndex}
      onNextClick={onNextClick}
      onBackClick={onBackClick}
      onExitTriggered={onExitWizard}
      showDesktopViewBreakpoint={showDesktopViewBreakpoint}
      {...rest}
    >
      <div className="MonthlyRentAndSecurityDeposit">
        <WizardAlert serverErrorObj={serverErrorObj} displayAt={DISPLAY_SERVER_ERROR_AT.PAGE} />

        <WizardFlexContainer>
          {/* cast as div to prevent cannot appear as a descendant of form error */}
          <Form asChild={true} onSubmit={(e: FormEvent<HTMLFormElement>) => e.preventDefault()}>
            <div>
              <FormSectionContainer />
            </div>
          </Form>
          {canAccessTopOfFunnelPremium ? (
            <WizardTipCardContainer
              header={premiumHeader}
              showDesktopViewBreakpoint={showDesktopViewBreakpoint}
              isPremiumTip={true}
            >
              {pricingGuidanceError ? (
                <Alert
                  appearance="error"
                  actionButton={<TextButton onClick={() => refetchPremiumPricingGuidance()}>Retry now</TextButton>}
                  body="We are having issues loading your pricing guidance data."
                />
              ) : (
                <PremiumPriceTipContent
                  isActiveProductTypePremium={isActiveProductTypePremium}
                  pricingGuidance={pricingGuidance || null}
                />
              )}
            </WizardTipCardContainer>
          ) : (
            !!rentZestimate && (
              <WizardTipCardContainer header={header} showDesktopViewBreakpoint={showDesktopViewBreakpoint}>
                <WizardTipContent />
              </WizardTipCardContainer>
            )
          )}
        </WizardFlexContainer>
      </div>
      <PremiumPricingGuidanceModal />
    </PageWizard>
  );
};

export default MonthlyRentAndSecurityDeposit;
